/**
 * api-fetch.js — small helpers around fetch + JSON parsing.
 *
 * Bug context (2026-05-06): on staging, several panels surfaced the raw
 * SyntaxError text "Unexpected token '<', '<HTML> <HE'..." when an
 * underlying API call returned an HTML response (Netlify Function timeout,
 * 502, 504, or auth-redirect HTML page) instead of JSON. The frontend
 * blindly called `await res.json()` and the SyntaxError propagated to the
 * user-visible error banner, which:
 *   (a) gave the user no idea what actually went wrong, and
 *   (b) hid the actual HTTP status that would have made the cause obvious.
 *
 * `parseJsonResponse(res)` is the small defensive wrapper. It tries to
 * read the body, parses it as JSON when the content-type looks JSON-ish
 * AND the body is non-empty, and falls back to a structured error when
 * the body is HTML or otherwise unparseable. The returned shape is the
 * SAME as what callers got before — a plain object — so existing callers
 * keep working without rewriting branches like `if (body.error) ...`.
 *
 * For the not-ok path we synthesize a `body.error` from the HTTP status
 * and a brief response-text snippet, so the user banner reads
 * "Server error (HTTP 504): function timed out after 26s" instead of
 * "Unexpected token '<'..."
 *
 * Streaming surfaces (`useStreamingBlueprint`) read SSE chunks directly
 * and never call this helper — they should not, the contract here is
 * for routes that return a JSON envelope.
 */

/**
 * Parse a fetch Response as JSON, defensively.
 *
 * Returns an object suitable for use in `if (!res.ok) setError(body.error)`
 * patterns. On parse failure or HTML responses, synthesizes a useful
 * `error` field with the HTTP status and a short body snippet.
 *
 * @param {Response} res
 * @returns {Promise<object>} parsed JSON body, or a synthesized error envelope
 */
export async function parseJsonResponse(res) {
  // Read the body once. We need text() rather than json() so we can
  // inspect the content type and surface a useful error for HTML.
  let text;
  try {
    text = await res.text();
  } catch (e) {
    return { error: `Network read failed (HTTP ${res.status}): ${e.message}` };
  }

  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || ct.includes("+json");

  // Empty body — fine for 204 No Content responses, error for everything else.
  if (!text) {
    if (res.ok) return {};
    return { error: `Server returned empty response (HTTP ${res.status})` };
  }

  // Try JSON parse first. We do this even when content-type isn't JSON,
  // because some functions return JSON without a content-type header.
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") return parsed;
    // JSON parsed to a primitive (rare). Wrap so the caller's
    // body.error / body.something access doesn't blow up.
    return { value: parsed };
  } catch {
    // Fall through to HTML / non-JSON handling below.
  }

  // Non-JSON body. Build a useful error message that surfaces the HTTP
  // status (the actually-informative bit) plus a short snippet of what
  // came back, so the user can see whether it's an auth-redirect page,
  // a Netlify timeout page, or something else. Cap the snippet so the
  // UI banner doesn't render a wall of HTML.
  const snippet = stripHtmlTags(text).trim().slice(0, 160);
  const statusHint = describeStatusHint(res.status);
  const message = snippet
    ? `Server error (HTTP ${res.status})${statusHint}: ${snippet}`
    : `Server error (HTTP ${res.status})${statusHint}`;
  return { error: message };
}

/**
 * Convenience wrapper: fetch + parseJsonResponse + ok check, returning
 * `{ ok, data, error }`. Lets callers replace
 *
 *     const res = await fetch(url, opts);
 *     const body = await res.json();
 *     if (!res.ok) setError(body.error || "...");
 *     else setX(body);
 *
 * with
 *
 *     const { ok, data, error } = await fetchJson(url, opts);
 *     if (!ok) setError(error);
 *     else setX(data);
 *
 * `data` is the parsed body when ok, undefined otherwise. `error` is
 * the message string when not ok, undefined otherwise.
 *
 * @param {string} url
 * @param {RequestInit} [opts]
 * @returns {Promise<{ok: boolean, status: number, data?: object, error?: string}>}
 */
export async function fetchJson(url, opts) {
  let res;
  try {
    res = await fetch(url, opts);
  } catch (e) {
    return { ok: false, status: 0, error: e.message || "Network error" };
  }
  const body = await parseJsonResponse(res);
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: body.error || `Request failed (HTTP ${res.status})`,
    };
  }
  return { ok: true, status: res.status, data: body };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Best-effort HTML-tag scrub. We're not parsing HTML, just turning
 * `<HTML> <HEAD>...<title>504 Gateway Timeout</title>...` into something
 * legible enough for the UI banner. Falls back to the raw text on the
 * (very unlikely) input that still contains angle brackets after the
 * regex pass.
 */
function stripHtmlTags(s) {
  if (typeof s !== "string") return "";
  return s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ");
}

/**
 * Map common HTTP statuses to a short user-comprehensible hint. Avoids
 * dumping hundreds of status codes — only the ones that are actually
 * common in our deploy.
 */
function describeStatusHint(status) {
  if (status === 401) return " — sign-in required";
  if (status === 403) return " — access denied";
  if (status === 404) return " — endpoint not found";
  if (status === 408 || status === 504) return " — request timed out";
  if (status === 429) return " — rate limited";
  if (status === 502 || status === 503) return " — service unavailable";
  if (status >= 500) return " — server error";
  if (status >= 400) return "";
  return "";
}
